import { Delete, DragIndicator, Edit } from '@mui/icons-material';
import {
  DndContext,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { type CSSProperties } from 'react';
import { QUESTION_TYPE_ACTIVITY_NON_NATIVE } from 'utils/config/reference';
import { htmlToPlainPreviewText } from './htmlToPlainPreviewText';
import s from './AssignmentBuilderQuestionPreview.module.css';

const SNIPPET_LEN = 80;

export type QuestionPreviewItem = {
  _uid: string;
  type: string;
  question?: string;
  [key: string]: unknown;
};

export type AssignmentBuilderQuestionPreviewProps = {
  questions: QuestionPreviewItem[];
  selectedUid: string | null;
  onSelect: (uid: string) => void;
  onDelete: (uid: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  layout?: 'multi' | 'single';
  singleIndex?: number;
  title?: string;
  emptyLabel?: string;
};

function previewHeadClass(type: string): string {
  if (type === 'trueFalse' || type === 'audioTrueFalse') return s.cardHeadTf;
  if (type === 'essay' || type === 'audioEssay') return s.cardHeadEssay;
  if (type === 'multipleChoice' || type === 'multipleResponse' || type === 'audioMultipleChoice') {
    return s.cardHeadQuiz;
  }
  return s.cardHeadDefault;
}

function typeLabel(type: string): string {
  const found = QUESTION_TYPE_ACTIVITY_NON_NATIVE.find((t) => t.value === type);
  return found?.type ?? type;
}

type DragHandleProps = {
  setNodeRef: (node: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners: Record<string, unknown> | undefined;
  style: CSSProperties;
  isDragging: boolean;
};

type PreviewCardShellProps = {
  question: QuestionPreviewItem;
  index: number;
  isActive: boolean;
  onSelect: (uid: string) => void;
  onDelete: (uid: string) => void;
  sortable: boolean;
  dragHandle?: DragHandleProps;
};

function PreviewCardShell({
  question: q,
  index,
  isActive,
  onSelect,
  onDelete,
  sortable,
  dragHandle,
}: PreviewCardShellProps) {
  const text = htmlToPlainPreviewText(String(q.question || ''));
  const preview = text.slice(0, SNIPPET_LEN);
  const count = text.length;

  return (
    <div
      ref={sortable && dragHandle ? dragHandle.setNodeRef : undefined}
      role="button"
      tabIndex={0}
      style={sortable && dragHandle ? dragHandle.style : undefined}
      className={`${s.card} ${isActive ? s.cardActive : ''} ${sortable && dragHandle?.isDragging ? s.cardDragging : ''}`}
      onClick={() => onSelect(q._uid)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(q._uid);
      }}
    >
      <div
        className={`${s.cardHead} ${previewHeadClass(q.type)} ${sortable ? s.cardHeadSortable : ''}`}
        {...(sortable && dragHandle ? dragHandle.attributes : {})}
        {...(sortable && dragHandle ? dragHandle.listeners : {})}
        onClick={sortable ? (e) => e.stopPropagation() : undefined}
        aria-label={sortable ? `Drag question ${index + 1}` : undefined}
      >
        <span className={s.typeLabel}>{typeLabel(q.type)}</span>
        {sortable ? <DragIndicator className={s.dragIcon} fontSize="small" aria-hidden /> : null}
      </div>
      <div className={s.cardBody}>{preview}</div>
      <div className={s.cardMeta}>
        <div className={s.metaLeft}>
          <span className={s.indexChip}>Q{index + 1}</span>
          <span className={s.charBadge}>
            {count} chars
          </span>
        </div>
        <div className={s.iconRow}>
          <button
            type="button"
            className={s.iconBtn}
            aria-label="Edit"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(q._uid);
            }}
          >
            <Edit fontSize="small" />
          </button>
          <button
            type="button"
            className={s.iconBtn}
            aria-label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(q._uid);
            }}
          >
            <Delete fontSize="small" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SortablePreviewCard(props: Omit<PreviewCardShellProps, 'sortable' | 'dragHandle'>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.question._uid });
  const lockedTransform = transform ? { ...transform, x: 0 } : null;
  const style: CSSProperties = {
    transform: CSS.Transform.toString(lockedTransform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 3 : 1,
  };
  return (
    <PreviewCardShell
      {...props}
      sortable
      dragHandle={{
        setNodeRef,
        attributes,
        listeners: listeners as Record<string, unknown> | undefined,
        style,
        isDragging,
      }}
    />
  );
}

export function AssignmentBuilderQuestionPreview({
  questions,
  selectedUid,
  onSelect,
  onDelete,
  onDragEnd,
  layout = 'multi',
  singleIndex = 0,
  title = 'Question Preview',
  emptyLabel = 'No questions yet.',
}: AssignmentBuilderQuestionPreviewProps) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 0, tolerance: 4 } }),
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  if (questions.length === 0) {
    return (
      <aside className={s.panel}>
        <header className={s.panelHeader}>
          <h3 className={s.panelTitle}>{title}</h3>
        </header>
        <div className={s.empty}>{emptyLabel}</div>
      </aside>
    );
  }

  if (layout === 'single') {
    const safeIndex = Math.min(Math.max(0, singleIndex), questions.length - 1);
    const q = questions[safeIndex];
    return (
      <aside className={s.panel}>
        <header className={s.panelHeader}>
          <h3 className={s.panelTitle}>{title}</h3>
          <p className={s.panelHint}>
            {safeIndex + 1} of {questions.length}
          </p>
        </header>
        <div className={s.listSingle}>
          <PreviewCardShell
            question={q}
            index={safeIndex}
            isActive={q._uid === selectedUid}
            onSelect={onSelect}
            onDelete={onDelete}
            sortable={false}
          />
        </div>
      </aside>
    );
  }

  return (
    <aside className={s.panel}>
      <header className={s.panelHeader}>
        <h3 className={s.panelTitle}>{title}</h3>
        <p className={s.panelHint}>
          {questions.length} question{questions.length === 1 ? '' : 's'} · drag to reorder
        </p>
      </header>
      <div className={s.list}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={questions.map((q) => q._uid)} strategy={verticalListSortingStrategy}>
            {questions.map((q, index) => (
              <SortablePreviewCard
                key={q._uid}
                question={q}
                index={index}
                isActive={q._uid === selectedUid}
                onSelect={onSelect}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </aside>
  );
}
